import { useState } from 'react'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import Drawer from '../../common/Drawer/Drawer'
import DrawerAvatar from '../../common/Drawer/DrawerAvatar'
import DrawerSection from '../../common/Drawer/DrawerSection'
import DrawerCard from '../../common/Drawer/DrawerCard'
import CreateEnrollmentWizard from '../../enrollments-billing/components/CreateEnrollmentWizard'
import MemberEnrollmentsTab from './MemberEnrollmentsTab'
import MemberEnrollmentDetail from './MemberEnrollmentDetail'

import avatarPlaceholder from '../../../assets/images/mock/default-profile-icon.png'

import { ReactComponent as PersonIcon } from '../../../assets/images/common/icons/person-icon.svg'
import { ReactComponent as FamilyIcon } from '../../../assets/images/common/icons/family-icon.svg'
import { ReactComponent as CallIcon } from '../../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as MailIcon } from '../../../assets/images/common/icons/mail-icon.svg'
import { ReactComponent as CopyIcon } from '../../../assets/images/common/icons/copy-icon.svg'

import { GENDER_LABEL_KEYS } from '../../../utility/userHelper'
import { formatPhoneForLinks } from '../../../utility/phoneHelper'

import classes from './DetailsDrawer.module.scss'
import colors from '../../../assets/styles/globals.scss'

const TABS = { contact: 'contact', enrollments: 'enrollments' }

const DetailsDrawer = ({ student, onClose }) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(TABS.contact)
  // When set, the drawer shows that enrollment's drill-down instead of the tabs.
  const [selectedEnrollment, setSelectedEnrollment] = useState(null)
  const [showCreateWizard, setShowCreateWizard] = useState(false)
  const [enrollmentsKey, setEnrollmentsKey] = useState(0)

  const familyAdmin = student.family?.users.filter(user => user.familyAdmin)[0]
  const studentGender = t(GENDER_LABEL_KEYS[student.gender])
  const adminGenderInitial = t(GENDER_LABEL_KEYS[familyAdmin.gender]).at(0)

  const handleCopy = (e, url) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard.writeText(url)
    toast.success(t('members.detailsDrawer.copiedEmail'))
  }

  return (
    <>
      <Drawer
        onClose={onClose}
        avatar={<DrawerAvatar src={student.avatar?.fileUrl || avatarPlaceholder} alt={`${student.firstName} ${student.lastName}`} />}
        title={`${student.firstName} ${student.lastName}`}
        subtitle={t('members.ageYrsGender', { age: student.age ?? '-', gender: studentGender })}
      >
        {selectedEnrollment && (
          <MemberEnrollmentDetail enrollment={selectedEnrollment} student={student} onBack={() => setSelectedEnrollment(null)} />
        )}

        {!selectedEnrollment && (
          <>
            <div className={classNames('d-flex', classes.tabList)}>
              {Object.values(TABS).map(tab => (
                <button
                  key={tab}
                  type='button'
                  className={classNames(classes.tab, { [classes.tabActive]: activeTab === tab })}
                  onClick={() => setActiveTab(tab)}
                >
                  {t(tab === TABS.contact ? 'members.detailsDrawer.contactInfo' : 'members.detailsDrawer.enrollments')}
                </button>
              ))}
            </div>

            {activeTab === TABS.contact && (
              <>
                <DrawerSection title={t('members.detailsDrawer.quickActions')}>
                  <DrawerCard
                    icon={<PersonIcon width={16} height={16} color={colors.primaryOrange} />}
                    label={t('common.action.viewProfile')}
                    to={`/profile/${student.id}`}
                  />
                  <DrawerCard
                    icon={<FamilyIcon width={16} height={16} color={colors.primaryOrange} />}
                    label={t('members.detailsDrawer.viewFamilyPage')}
                    to={`/members/families/${student.family.id}`}
                  />
                </DrawerSection>

                <DrawerSection title={t('members.contactFamilyAdmin')}>
                  <DrawerCard
                    image={familyAdmin.avatar?.fileUrl || avatarPlaceholder}
                    iconVariant='avatar'
                    label={(
                      <>
                        {familyAdmin.firstName} {familyAdmin.lastName}
                        <span className={`${classes.familyAdminLabel} ml-2`}>{t('members.familyAdmin')}</span>
                      </>
                    )}
                    note={t('members.ageGenderLabel', { age: familyAdmin.age ?? '-', gender: adminGenderInitial })}
                  />
                  {familyAdmin.phonePrefix && familyAdmin.phone && (
                    <DrawerCard
                      icon={<CallIcon width={16} height={16} className={classes.contactIcon} />}
                      label={`${familyAdmin.phonePrefix} ${familyAdmin.phone}`}
                      href={`tel:${formatPhoneForLinks(familyAdmin.phonePrefix, familyAdmin.phone)}`}
                    />
                  )}
                  <DrawerCard
                    icon={<MailIcon width={16} height={16} className={classes.contactIcon} />}
                    label={familyAdmin.email || '-'}
                    href={familyAdmin.email ? `mailto:${familyAdmin.email}` : undefined}
                    trailing={familyAdmin.email && (
                      <button
                        onClick={(e) => handleCopy(e, familyAdmin.email)}
                        className={`${classes.copyButton} d-inline-flex align-items-center`}
                        aria-label={t('members.detailsDrawer.copyEmailAria')}
                      >
                        <CopyIcon width={16} height={16} />
                      </button>
                    )}
                  />
                </DrawerSection>
              </>
            )}

            {activeTab === TABS.enrollments && (
              <MemberEnrollmentsTab
                key={enrollmentsKey}
                student={student}
                onSelect={setSelectedEnrollment}
                onCreateEnrollment={() => setShowCreateWizard(true)}
              />
            )}
          </>
        )}
      </Drawer>

      {showCreateWizard && (
        <CreateEnrollmentWizard
          student={student}
          onClose={() => setShowCreateWizard(false)}
          onCreated={() => setEnrollmentsKey(key => key + 1)}
        />
      )}
    </>
  )
}

DetailsDrawer.propTypes = {
  student: PropTypes.object.isRequired,
  onClose: PropTypes.func,
}

export default DetailsDrawer
